import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, Loading } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

export const PRODUCT_MOV_LABEL = {
  ENTRADA: "Entrada",
  VENDA: "Venda",
  SAIDA_PARCEIRO: "Saída para parceiro",
  DEVOLUCAO_PARCEIRO: "Devolução de parceiro",
  AJUSTE: "Ajuste",
};

export function ProductStockModal({ product, onClose, onSaved }) {
  const variations = (product.variations || []).filter((v) => v.active !== false);
  const [type, setType] = useState("ENTRADA");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [variationId, setVariationId] = useState(variations[0]?.id || "");

  const current = variations.length
    ? Number(variations.find((v) => v.id === variationId)?.stock_units || 0)
    : Number(product.stock_units || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (variations.length && !variationId) {
      toast.error("Escolha a cor que você quer movimentar.");
      return;
    }
    if (!Number(quantity)) {
      toast.error("Informe a quantidade da movimentação.");
      return;
    }
    if (preview < 0) {
      toast.error(
        `Esta movimentação deixaria o estoque negativo. Disponível: ${current.toLocaleString(
          "pt-BR"
        )} un`
      );
      return;
    }
    setBusy(true);
    try {
      await api.post("/product-movements", {
        product_id: product.id,
        variation_id: variationId || null,
        type,
        quantity: Number(quantity),
        notes,
      });
      toast.success("Estoque atualizado com sucesso.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const delta = Number(quantity || 0);
  const preview =
    type === "ENTRADA" || type === "DEVOLUCAO_PARCEIRO" || type === "AJUSTE"
      ? current + delta
      : current - Math.abs(delta);

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Movimentar estoque"
      description={`${product.name} · ${current.toLocaleString("pt-BR")} un em estoque${
        variations.length ? " nesta cor" : ""
      }`}
      testid="product-stock-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        {variations.length > 0 && (
          <Field label="Cor / combinação" hint="O estoque acabado é controlado por combinação.">
            <NativeSelect
              value={variationId}
              onChange={setVariationId}
              data-testid="stock-mov-variation-select"
              options={variations.map((v) => ({
                value: v.id,
                label: `${v.name} · ${Number(v.stock_units || 0).toLocaleString("pt-BR")} un`,
              }))}
            />
          </Field>
        )}
        <Field label="Tipo de movimentação">
          <NativeSelect
            value={type}
            onChange={setType}
            data-testid="stock-mov-type-select"
            options={[
              { value: "ENTRADA", label: "Entrada (produção concluída ou compra)" },
              { value: "AJUSTE", label: "Ajuste (correção de contagem)" },
            ]}
          />
        </Field>
        <Field
          label="Quantidade (unidades)"
          hint={type === "AJUSTE" ? "Use número negativo para reduzir o estoque." : undefined}
        >
          <Input
            type="number"
            step="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="font-num"
            data-testid="stock-mov-quantity-input"
          />
        </Field>
        <Field label="Observação">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="stock-mov-notes-input" />
        </Field>
        <div className="flex items-center justify-between rounded-lg border border-brand/30 bg-brand/10 px-4 py-3 text-sm">
          <span className="text-muted-foreground">Novo estoque</span>
          <span
            className={`font-num text-lg ${preview < 0 ? "text-red-300" : "text-brand"}`}
            data-testid="stock-mov-preview"
          >
            {preview.toLocaleString("pt-BR")} un
          </span>
        </div>
        <Button type="submit" disabled={busy} data-testid="save-stock-mov-btn" className="h-11 rounded-full font-semibold transition-transform active:scale-95">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar movimentação
        </Button>
      </form>
    </ResponsiveModal>
  );
}

export function ProductHistoryModal({ product, onClose }) {
  const [moves, setMoves] = useState(null);
  const varName = Object.fromEntries((product.variations || []).map((v) => [v.id, v.name]));

  useEffect(() => {
    api
      .get(`/products/${product.id}/history`)
      .then(({ data }) => setMoves(data))
      .catch((e) => toast.error(apiError(e)));
  }, [product.id]);

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Histórico do produto"
      description={product.name}
      testid="product-history-modal"
    >
      {moves === null ? (
        <Loading label="Carregando histórico..." testid="product-history-loading" />
      ) : moves.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="product-history-empty">
          Nenhuma movimentação registrada ainda.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-white/10" data-testid="product-history-list">
          {moves.map((m) => (
            <li key={m.id} className="flex items-center gap-3 py-3">
              <span className="w-14 shrink-0 font-num text-xs text-muted-foreground">{dataBR(m.created_at)}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">
                  {PRODUCT_MOV_LABEL[m.type] || m.type}
                  {m.variation_id && varName[m.variation_id] && (
                    <span className="ml-1.5 text-xs text-brand">{varName[m.variation_id]}</span>
                  )}
                </p>
                {m.notes && <p className="truncate text-xs text-muted-foreground">{m.notes}</p>}
              </div>
              <span className={`font-num text-sm ${m.quantity >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                {m.quantity >= 0 ? "+" : "−"}
                {Math.abs(m.quantity).toLocaleString("pt-BR")} un
              </span>
            </li>
          ))}
        </ul>
      )}
    </ResponsiveModal>
  );
}
